import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { commitSharedAuthToken, clearSharedAuthToken } from './sharedAuthState';

export interface FrontendUser {
  id: string;
  email: string;
  name: string;
  role: string;
  org_id?: string;
}

export interface CognitoAuthResult {
  idToken: string;
  user: FrontendUser;
  expiresAtEpochSeconds: number | null;
}

export interface CognitoAuthError extends Error {
  code?: string;
}

// Recognized ``custom:role`` values.  Mirrors the backend role vocabulary —
// anything outside this set fails closed rather than defaulting to farmer.
const KNOWN_ROLES: ReadonlySet<string> = new Set([
  'superadmin',
  'owner',
  'admin',
  'coop_admin',
  'org_admin',
  'farm_manager',
  'farmer',
  'technician',
  'viewer',
]);

// Roles allowed to issue physical valve commands.  Read-only roles
// (technician, viewer) and unknown/missing roles are never physical-capable.
export const PHYSICAL_CONTROL_ROLES: ReadonlySet<string> = new Set([
  'superadmin',
  'owner',
  'admin',
  'coop_admin',
  'org_admin',
  'farm_manager',
  'farmer',
]);

export const canIssuePhysicalCommands = (role: string | undefined | null): boolean => {
  const normalized = typeof role === 'string' ? role.trim().toLowerCase() : '';
  return normalized !== '' && PHYSICAL_CONTROL_ROLES.has(normalized);
};

let userPool: CognitoUserPool | null = null;

const requiredEnv = (name: string, value?: string): string => {
  if (!value) throw new Error(`Missing frontend configuration: ${name}`);
  return value;
};

const pool = (): CognitoUserPool => {
  if (!userPool) {
    userPool = new CognitoUserPool({
      UserPoolId: requiredEnv(
        'REACT_APP_COGNITO_USER_POOL_ID',
        process.env.REACT_APP_COGNITO_USER_POOL_ID,
      ),
      ClientId: requiredEnv(
        'REACT_APP_COGNITO_APP_CLIENT_ID',
        process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
      ),
      // Keep Cognito's refresh/access session tab-scoped. The application
      // persists only the IdToken under the legacy-compatible "token" key.
      Storage: window.sessionStorage,
    });
  }
  return userPool;
};

export const mapIdTokenClaims = (claims: Record<string, unknown>): FrontendUser => {
  const id = String(claims.sub || '');
  const email = String(claims.email || claims['cognito:username'] || '');
  if (!id || !email) throw new Error('Cognito IdToken is missing required user claims');

  // Fail closed: never synthesize a default role.  A missing, null, empty,
  // whitespace-only or unknown ``custom:role`` aborts session establishment so
  // no physical-control capability can ever be granted implicitly.
  const rawRole = claims['custom:role'];
  const role = rawRole === undefined || rawRole === null
    ? ''
    : String(rawRole).trim().toLowerCase();
  if (role === '' || !KNOWN_ROLES.has(role)) {
    throw new Error('Cognito IdToken is missing a valid custom:role claim');
  }

  return {
    id,
    email,
    name: String(claims.name || email),
    org_id: claims['custom:org_id'] ? String(claims['custom:org_id']) : undefined,
    role,
  };
};

export const persistIdToken = (idToken: string): string => {
  // Route through the shared-auth helper so the mutation revision advances in
  // lock-step with the shared token (ABA detection).
  return commitSharedAuthToken(idToken);
};

const expirationFromClaims = (claims: Record<string, unknown>): number | null => {
  const expiration = Number(claims.exp);
  return Number.isFinite(expiration) && expiration > 0 ? expiration : null;
};

// Build a *candidate* auth result from an already-decoded IdToken payload.
// Validates every required claim (including ``custom:role``) but performs NO
// side effects. Persisting the token to localStorage is a separate "commit"
// step owned by the caller (login, or the generation-guarded AuthContext).
// Throws when any required claim is invalid.
export const buildAuthResult = (
  idToken: string,
  payload: Record<string, unknown>,
): CognitoAuthResult => {
  const user = mapIdTokenClaims(payload); // throws on invalid role
  return { idToken, user, expiresAtEpochSeconds: expirationFromClaims(payload) };
};

const resultFromSession = (session: CognitoUserSession): CognitoAuthResult => {
  const token = session.getIdToken();
  return buildAuthResult(token.getJwtToken(), token.payload);
};

const decodeJwtPayload = (jwt: string): Record<string, unknown> => {
  const segments = jwt.split('.');
  if (segments.length !== 3 || !segments[1]) {
    throw new Error('Stored token is not a JWT');
  }

  const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = window.atob(padded);
  const encoded = Array.from(binary, (char) =>
    `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`,
  ).join('');
  const json = decodeURIComponent(encoded);
  const decoded = JSON.parse(json);
  if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) {
    throw new Error('Stored token payload is invalid');
  }
  return decoded as Record<string, unknown>;
};

// FIELD-AUTH-SESSION-PERSISTENCE: validate the shared application IdToken
// independently of the tab-scoped Cognito SDK session. This is used both when
// a refresh/direct navigation loses the SDK current-user pointer and when a
// different tab changes the browser-wide account. The token remains bounded by
// its original expiry and backend JWT validation stays authoritative.
//
// Read-only: on an invalid/expired token this returns null WITHOUT clearing
// localStorage. The clear is a "commit" decision owned by the caller, which
// must first confirm this restore is still current — a stale failed restore
// must never remove a newer account's shared token.
export const restorePersistedCognitoIdToken = (): CognitoAuthResult | null => {
  const idToken = localStorage.getItem('token');
  if (!idToken) return null;

  try {
    const payload = decodeJwtPayload(idToken);
    const expiration = Number(payload.exp);
    const clientId = requiredEnv(
      'REACT_APP_COGNITO_APP_CLIENT_ID',
      process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
    );

    if (
      !Number.isFinite(expiration)
      || expiration <= Math.floor(Date.now() / 1000)
      || payload.token_use !== 'id'
      || payload.aud !== clientId
    ) {
      throw new Error('Stored Cognito IdToken is expired or invalid');
    }

    return {
      idToken,
      user: mapIdTokenClaims(payload),
      expiresAtEpochSeconds: expiration,
    };
  } catch {
    return null;
  }
};

// Sign out only the Cognito SDK session belonging to this tab.  Deliberately
// leaves the shared application IdToken untouched so a storage event from
// another tab can safely synchronize this tab to the newly active account.
export const clearCognitoTabSession = (): void => {
  pool().getCurrentUser()?.signOut();
};

const invalidRoleError = (): CognitoAuthError => {
  const err: CognitoAuthError = new Error('Cognito session is missing a valid role claim.');
  err.code = 'auth:invalidRole';
  return err;
};

// Deterministic, non-sensitive rejection for a login that was superseded by a
// newer authoritative transition (a cross-tab storage switch or an explicit
// logout) while authentication was in flight. Never leaks claims or tenant
// identity; the caller surfaces it as a stable frontend error code.
export const sessionSupersededError = (): CognitoAuthError => {
  const err: CognitoAuthError = new Error('Auth session was superseded by a newer account change.');
  err.code = 'auth:sessionSuperseded';
  return err;
};

const friendlyCognitoError = (error: unknown): CognitoAuthError => {
  const candidate = (error || {}) as { code?: string; message?: string };
  const err: CognitoAuthError = new Error(candidate.message || 'auth:cognitoFailure');
  if (candidate.code === 'NotAuthorizedException' || candidate.code === 'UserNotFoundException') {
    err.code = 'auth:loginError';
  } else if (candidate.code === 'UserNotConfirmedException') {
    err.code = 'auth:userNotConfirmed';
  } else if (candidate.code === 'PasswordResetRequiredException') {
    err.code = 'auth:passwordResetRequired';
  } else {
    err.code = 'auth:cognitoFailure';
  }
  return err;
};

export const authenticateWithCognito = (
  email: string,
  password: string,
): Promise<CognitoAuthResult> =>
  new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool() });
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        try {
          // Side-effect-free: produce a candidate only. The caller (AuthContext)
          // performs the single authoritative commit once it confirms this login
          // is still the current generation — a login superseded by a cross-tab
          // storage switch must not write the shared token.
          resolve(resultFromSession(session));
        } catch {
          // Fail closed without clearing: a failed login must not disturb the
          // current browser-wide account.
          reject(invalidRoleError());
        }
      },
      onFailure: (error) => reject(friendlyCognitoError(error)),
      newPasswordRequired: () => {
        const err: CognitoAuthError = new Error('Cognito new password must be set by an administrator.');
        err.code = 'auth:newPasswordRequired';
        reject(err);
      },
      mfaRequired: () => {
        const err: CognitoAuthError = new Error('Cognito MFA challenge is not yet supported by this interface.');
        err.code = 'auth:mfaRequired';
        reject(err);
      },
      totpRequired: () => {
        const err: CognitoAuthError = new Error('Cognito TOTP challenge is not yet supported by this interface.');
        err.code = 'auth:totpRequired';
        reject(err);
      },
    });
  });

// Side-effect-free session restore. Produces a *candidate* auth result (or
// null / a rejection) WITHOUT mutating shared localStorage. The caller is
// responsible for committing an accepted result only after confirming this
// restore is still current — a superseded background restore must never win
// over a newer account event.
export const restoreCognitoSession = (): Promise<CognitoAuthResult | null> =>
  new Promise((resolve, reject) => {
    const cognitoUser = pool().getCurrentUser();
    if (!cognitoUser) {
      resolve(restorePersistedCognitoIdToken());
      return;
    }
    cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) {
        // Do NOT clear localStorage here: a newer account's shared token may
        // already be in place, and the caller decides whether to clear.
        reject(friendlyCognitoError(error));
        return;
      }
      try {
        // Build the candidate only — persisting is the caller's commit step.
        resolve(resultFromSession(session));
      } catch {
        // Fail closed WITHOUT clearing: a stale restore must not remove a
        // newer account's shared token.
        reject(invalidRoleError());
      }
    });
  });

export const changeCognitoPassword = (
  currentPassword: string,
  newPassword: string,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const cognitoUser = pool().getCurrentUser();
    if (!cognitoUser) {
      const err: CognitoAuthError = new Error('No active Cognito session.');
      err.code = 'auth:noActiveSession';
      reject(err);
      return;
    }
    cognitoUser.getSession((sessionError: Error | null, session: CognitoUserSession | null) => {
      if (sessionError || !session?.isValid()) {
        reject(friendlyCognitoError(sessionError));
        return;
      }
      cognitoUser.changePassword(currentPassword, newPassword, (error) => {
        if (error) reject(friendlyCognitoError(error));
        else resolve();
      });
    });
  });

export const signOutCognito = (): void => {
  clearCognitoTabSession();
  clearSharedAuthToken();
};
